import { ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";

interface CollapsibleSectionProps {
  title: string;
  description?: string;
  children: ReactNode;
  defaultOpen?: boolean;
  scrollIntoViewOnOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const CollapsibleSection = ({
  title,
  description,
  children,
  defaultOpen = true,
  scrollIntoViewOnOpen,
  onOpenChange,
}: CollapsibleSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  return (
    <Collapsible
      scrollIntoViewOnOpen={scrollIntoViewOnOpen}
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <Card>
        <CardHeader className="flex flex-row justify-between items-center space-y-0 gap-4 p-2">
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className="p-2 w-full flex flex-col items-start gap-0 h-auto"
            >
              <div className="flex items-center gap-2">
                <ChevronDown
                  className={cn(
                    "transition-transform duration-200 shrink-0",
                    isOpen ? "rotate-180" : "rotate-0",
                  )}
                />
                <CardTitle className="text-base">{title}</CardTitle>
              </div>
              {description && (
                <p className="text-sm text-muted-foreground pl-6">
                  {description}
                </p>
              )}
            </Button>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent>{children}</CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};
